import { useCallback, useMemo, useState } from "react"
import { GetApp } from "../../App"
import { logger } from "../../App/Logger"
import { ManualShipStatusAdvice } from "./ManualShipStatusAdvice"
import { BlockField, BlockRow } from "../../Components/Block"
import { UserAdviceDialog } from "../../Components/UserAdvice/UserAdvice"
import { OverlayViewDefinition } from "../OverlayView"
import { UserAdviceViewsData } from "./Context"
import { UserAdviceManualShipStatusViewProps } from "./Params"

// The control panel for the manual ship-status mode (#511) -- the mode the crew
// switches on (from the LogButton menu) to suppress ALL automatic ship-status
// changes for a while and set the status themselves: for a very slow departure
// manoeuvre, or while circling in front of a bascule bridge.
//
// The advice button carries the mode's end time; this dialog adds the remaining
// minutes and the only two actions the design allows: EXTEND (restart the timeout)
// and CANCEL (end it now). Both are the same route -- `enabled: true` on a running
// mode rewrites its `expiresAt` in place -- so extending needs no endpoint of its
// own.
//
// It deliberately implements NO expiry: navigation owns that (by timeout and at the
// day change) and closes the status, which makes this advice disappear on its own.
export function UserAdviceManualShipStatusView({ advice, state, initFailed }: UserAdviceManualShipStatusViewProps) {
  const app = GetApp();
  const t = app.getT("UserAdviceManualShipStatusView");

  const [failed, setFailed] = useState(initFailed ?? false);

  const userAdvice = useMemo(() => advice.parent as ManualShipStatusAdvice, [advice]);

  // Only dismiss the dialog once the update actually succeeds -- e.g. a user without
  // `editCurrent` gets a 401, which must stay visible instead of being swallowed
  // behind an optimistically-closed dialog (#1146).
  const submit = useCallback(async (enabled: boolean) => {
    setFailed(false);
    try {
      await UserAdviceViewsData.manualShipStatus(app.apiClient, { enabled });
      state.setSelected(undefined);
    } catch (e) {
      logger.error("manual ship status update", e);
      setFailed(true);
    }
  }, [app, state]);

  return (
    <UserAdviceDialog className="UserAdviceManualShipStatusView">
      <BlockRow className="block-row-infotext">
        {t("text")}
      </BlockRow>
      <BlockRow className="block-row-infotext">
        {t("remaining", { minutes: userAdvice.remainingMinutes(), time: userAdvice.endTime(app) })}
      </BlockRow>
      {failed && <BlockRow><BlockField><span className="UserAdviceManualShipStatusView-error text-error">{t("error")}</span></BlockField></BlockRow>}
      <BlockRow actions={true} className="block-row-no_labels">
        <BlockField><button className="button" onClick={() => state.setSelected(undefined)}>{t("close")}</button></BlockField>
        <BlockField><button className="button" onClick={() => submit(false)}>{t("cancel")}</button></BlockField>
        <BlockField><button className="button default" onClick={() => submit(true)}>{t("extend")}</button></BlockField>
      </BlockRow>
    </UserAdviceDialog>
  );
}

// This dialog's own overlay attributes (#1171), declared here rather than in
// `UserAdviceOverlayView` so the dialog keeps deciding how it presents itself. It
// takes its title straight from the advice whose button opened it, and stays
// closable unless that advice asked otherwise. Deliberately hook-free -- one of
// these is called per open advice, so a hook would vary React's hook count.
export function userAdviceManualShipStatusOverlay(props: UserAdviceManualShipStatusViewProps): OverlayViewDefinition {
  return {
    close: !props.advice.props.hideClose,
    title: props.advice.props.title,
    render: () => <UserAdviceManualShipStatusView {...props} />,
  };
}
